/* eslint-disable indent */
/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'emotion-theming';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import StaticAddColumnDropdownSimple from './StaticAddColumnDropdownSimple';
import StatusBadgeScroller from './StatusBadgeScroller';
import SwapPropDropdownSAQC from './SwapPropDropdownSAQC';
import Text from '../../../../shared/components/Text/Text';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import { normalizeRepToken } from '../../../../shared/utils/normalizeRepToken';
import wipeRepKeywordOnMutation from '../../../../shared/utils/wipeRepKeywordOnMutation';
import { FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';

/**
 * Local (non-persisting) twin of ExerciseTableTransposedSAQC — the SAQ/cardio
 * counterpart to ExerciseTableTransposedLocal. Same transposed layout, one
 * input per set, commas blocked; all edits stay local and reach the parent
 * through `onChange`.
 *
 * See ExerciseTableTransposedLocal for the full rationale. Two SAQC-specific
 * deviations from the saved table:
 *  - Label tooltips use `ReactTooltip` rather than `TBportalTooltip`. The
 *    portal variant positions itself against the calendar's scroll container
 *    (`calendarContainerScrollPositionTop`), which doesn't exist inside a modal.
 *  - `intensityUnit` / `restPerRepUnit` are surfaced through `onChange` so the
 *    wizard can send `repIntensityUnit` / `repRestUnit`.
 */

const RANGE_FIELDS = new Set(['prescribed value']);

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : props.theme.colors.text)};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : props.theme.colors.text)};
  }
`;

const RowValueContainer = styled('div')`
  width: 100%;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3px;
`;

/** Transposed-layout styled components — properties as rows, sets as columns. */
const SetsCountHeader = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 0 22px 6px;
`;

const SetsFieldGroup = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const SetsSubtext = styled('div')`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  white-space: nowrap;
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StateBadge = styled('div')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 8px;
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: 11px;
  white-space: nowrap;
`;

const SetsCountLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const SetsStepper = styled('div')`
  display: inline-flex;
  align-items: stretch;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surfaceInput};
  overflow: hidden;
`;

const StepperButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: ${TransitionGlobal};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHoverStrong};
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: none;
    background: ${FOCUS_FILL_VAR};
    box-shadow: inset 0 0 0 2px #BFDBFE;
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.textDisabled};
    cursor: not-allowed;
  }
`;

const StepperInput = styled('input')`
  width: 35px;
  border: none;
  border-left: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: transparent;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus {
    outline: none;
  }
`;

const TransposedScroll = styled('div')`
  width: 100%;
  overflow-x: auto;
  padding-left: 6px;
`;

const TransposedTable = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TransposedHeaderRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  width: 100%;
`;

const TransposedRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  width: 100%;
`;

const PropertyLabelCell = styled('div')`
  display: flex;
  align-items: center;
  min-width: 150px;
  max-width: 150px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text};
`;

const SetHeaderCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 80px;
  min-width: 80px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => (theme.isDark ? theme.colors.textSecondary : theme.colors.text)};
`;

/** Per-set cell. Zeroes out descendant min-widths because the legacy
 * `RowValueContainer` carries `min-width: 180px` from the row-per-set layout
 * and the `<input>` default `size=20` would otherwise prevent shrink. */
const SetCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 80px;
  min-width: 80px;
  > div {
    min-width: 0;
    width: 100%;
  }
  input {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const TransposedFooter = styled('div')`
  display: flex;
  align-items: center;
  padding-top: 12px;
`;

const AddPropertyTrigger = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px dashed ${({ theme }) => theme.colors.borderMedium};
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition: ${TransitionGlobal};
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const AddPropertyTriggerIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
`;

const ExerciseTableTransposedSAQCLocal = ({
  content,
  initialSets,
  onChange,
  coachCompletion,
  completionOnly,
}) => {
  const { colors } = useTheme();
  /** Namespaced so tooltip ids and focus lookups can't collide with the
   * calendar row for this same assignedId rendered behind the modal. */
  const domKey = `ufo-saqc-${content?.assignedId}`;
  const repsTooltipId = `${domKey}-reps-tooltip`;
  const athleteInputTooltipId = `${domKey}-athlete-input-tooltip`;
  const restTooltipId = `${domKey}-rest-tooltip`;

  const intensityKey = `intensity (${content.repIntensityUnit || 'bpm'})`;
  const restPerRepKey = `rest per rep (${content.repRestUnit || 'secs'})`;

  const [rest, setRest] = useState(content?.restTime || null);
  const [intensityUnit, setIntensityUnit] = useState(content?.repIntensityUnit || null);
  const [restPerRepUnit, setRestPerRepUnit] = useState(content?.repRestUnit || null);

  /** Athlete input and prescribed value are dropped for completion-only
   * exercises — there is nothing for the coach to prescribe. */
  const isCompletionOnlyExercise = content?.exercise?.inputType === 'C';

  const buildPrimaryRows = () => (isCompletionOnlyExercise ? [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'reps' },
    { name: restPerRepKey },
    { name: intensityKey },
  ] : [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'athlete input', label: `${capitalizeWords(content?.exercise?.inputTypeDescription)} (${content?.exercise?.unit === 'MS' ? 'm:s' : content?.exercise?.unitAbbr})` },
    { name: 'prescribed value', label: 'Prescribed Value' },
    { name: 'reps' },
    { name: restPerRepKey },
    { name: intensityKey },
  ]);

  const [primaryRows, setPrimaryRows] = useState(buildPrimaryRows);

  const defaultPrimaryRowSwitch = isCompletionOnlyExercise
    ? {
      sets: true,
      rest: false,
      reps: false,
      [restPerRepKey]: false,
      [intensityKey]: false,
    }
    : {
      sets: true,
      rest: false,
      'athlete input': true,
      'prescribed value': false,
      reps: false,
      [restPerRepKey]: false,
      [intensityKey]: false,
    };

  /** Plain state, NOT useSessionState — that hook keys on assignedId and this
   * table edits the same assignedId as the live calendar row. */
  const [primaryRowSwitch, setPrimaryRowSwitchState] = useState(defaultPrimaryRowSwitch);

  const keySwapDictionary = {
    'prescribed value': 'prescribedValue',
    set: 'setNumber',
    reps: 'reps',
    'intensity (bpm)': 'intensityValue',
    'intensity (RPE)': 'intensityValue',
    'intensity (%)': 'intensityValue',
    'intensity (W)': 'intensityValue',
    'rest per rep (secs)': 'restPerRep',
    'rest per rep (bpm)': 'restPerRep',
  };

  const [rows, setRows] = useState(initialSets || content?.sets || []);

  /** Draft string shown while the Sets field is focused; committed on
   * blur/Enter so a two-digit count never transiently collapses the table. */
  const [setsDraft, setSetsDraft] = useState(null);

  const tableContainerRef = useRef(null);

  /** Turn on the columns the item already carries data for. Runs once. */
  useEffect(() => {
    if (!content?.sets) return;
    const nextSwitch = { ...defaultPrimaryRowSwitch };
    let hasIntensityData = false;

    content.sets.forEach((set) => {
      if (set.prescribedValue !== null && set.prescribedValue !== undefined && set.prescribedValue !== '') {
        nextSwitch['prescribed value'] = true;
      }
      if (set.restPerRep !== null && set.restPerRep !== undefined && set.restPerRep !== '') {
        nextSwitch[restPerRepKey] = true;
      }
      if (set.reps !== null && set.reps !== undefined && set.reps !== '') {
        nextSwitch.reps = true;
      }
      if (set.intensityValue !== null && set.intensityValue !== undefined && set.intensityValue !== '') {
        hasIntensityData = true;
      }
    });

    if (content.restTime) {
      nextSwitch.rest = true;
    }
    if (content.repIntensityUnit || hasIntensityData) {
      nextSwitch[intensityKey] = true;
    }

    setPrimaryRowSwitchState(nextSwitch);
  }, []);

  /** The single seam back to the parent — it decides when to submit. */
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current({
      sets: rows,
      rest,
      intensityUnit,
      restPerRepUnit,
      primaryRowSwitch,
    });
  }, [rows, rest, intensityUnit, restPerRepUnit, primaryRowSwitch]);

  // Track if all optional columns are enabled to hide the add column button
  const [showAddColumnButton, setShowAddColumnButton] = useState(true);

  useEffect(() => {
    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
      && primaryRowSwitch['prescribed value']
      && primaryRowSwitch.reps
      && (primaryRowSwitch['rest per rep (secs)'] || primaryRowSwitch['rest per rep (bpm)'])
      && (primaryRowSwitch['intensity (bpm)'] || primaryRowSwitch['intensity (RPE)'] || primaryRowSwitch['intensity (%)'] || primaryRowSwitch['intensity (W)']);

    setShowAddColumnButton(!allOptionalPropertiesEnabled);
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch['prescribed value'],
    primaryRowSwitch.reps,
    primaryRowSwitch['rest per rep (secs)'],
    primaryRowSwitch['rest per rep (bpm)'],
    primaryRowSwitch['intensity (bpm)'],
    primaryRowSwitch['intensity (RPE)'],
    primaryRowSwitch['intensity (%)'],
    primaryRowSwitch['intensity (W)'],
  ]);

  /**
   * Toggles a property column. Fields for switched-off columns are stripped
   * from every row so their values don't linger in the payload, and the
   * intensity / rest-per-rep unit follows whichever variant is now active.
   */
  const setPrimaryRowSwitch = (newState) => {
    const resolved = typeof newState === 'function' ? newState(primaryRowSwitch) : newState;

    setRows((prevRows) => prevRows.map((row) => {
      const cleanedRow = { ...row };
      Object.keys(resolved).forEach((key) => {
        if (!resolved[key] && keySwapDictionary[key]) {
          delete cleanedRow[keySwapDictionary[key]];
        }
      });
      return cleanedRow;
    }));

    if (!resolved.rest) {
      setRest(null);
    }

    if (!resolved['intensity (bpm)'] && !resolved['intensity (RPE)'] && !resolved['intensity (%)'] && !resolved['intensity (W)']) {
      setIntensityUnit(null);
    } else if (resolved['intensity (bpm)']) {
      setIntensityUnit('bpm');
    } else if (resolved['intensity (RPE)']) {
      setIntensityUnit('RPE');
    } else if (resolved['intensity (%)']) {
      setIntensityUnit('%');
    } else if (resolved['intensity (W)']) {
      setIntensityUnit('W');
    }

    if (!resolved['rest per rep (secs)'] && !resolved['rest per rep (bpm)']) {
      setRestPerRepUnit(null);
    } else if (resolved['rest per rep (secs)']) {
      setRestPerRepUnit('secs');
    } else if (resolved['rest per rep (bpm)']) {
      setRestPerRepUnit('bpm');
    }

    setPrimaryRowSwitchState(resolved);
  };

  const updateColumnName = useCallback((oldName, newName) => {
    setPrimaryRows(
      (prevRows) => prevRows.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitchState((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  /** Set the count directly (1–10). Grows by cloning the last row, shrinks
   * by slicing — data in dropped sets is discarded. */
  const setSetCount = (target) => {
    const clamped = Math.max(1, Math.min(10, target));
    if (clamped === rows.length) return;
    let updatedRows;
    if (clamped > rows.length) {
      const lastRow = rows[rows.length - 1] || {};
      const additions = Array.from(
        { length: clamped - rows.length },
        (_, i) => ({ ...lastRow, setNumber: rows.length + i + 1 }),
      );
      updatedRows = [...rows, ...additions];
    } else {
      updatedRows = rows.slice(0, clamped).map((row, i) => ({ ...row, setNumber: i + 1 }));
    }
    setRows(updatedRows);
  };

  const commitSetsDraft = () => {
    if (setsDraft !== null && setsDraft !== '') {
      setSetCount(Math.min(10, Math.max(1, parseInt(setsDraft, 10))));
    }
    setSetsDraft(null);
  };

  /** Auto-focus the first input of a newly added property (skips
   * disabled / athlete-input-only fields, which carry no data-add-focus). */
  const focusAddedProperty = useCallback((name) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-add-focus="${domKey}-${name}"]`);
      if (el && !el.disabled) {
        el.focus();
        if (typeof el.select === 'function') el.select();
      }
    }, 50);
  }, [domKey]);

  const parseFieldValue = (field, value) => {
    if (RANGE_FIELDS.has(field) || field.includes('rest per rep')) {
      // Send range/+ tokens as strings; pure numbers as floats
      if (!value && value !== 0) return '';
      if (/^\d+(\.\d+)?$/.test(String(value))) return parseFloat(value);
      return value;
    }
    return value;
  };

  const handleInputChange = (rowIndex, field, value) => {
    // Prevent negative values for numeric fields
    if ((field === 'prescribed value' || field.includes('rest per rep') || field === 'reps' || field.includes('intensity')) && parseFloat(value) < 0) {
      return;
    }

    let normalizedFieldValue = value;
    if (field === 'reps') {
      // Allow AMAP
      if (value && value.toUpperCase() === 'AMAP') {
        // AMAP is valid, continue
      } else if (value && value.includes('-') && !value.startsWith('-')) {
        // Check for ranges (e.g., "8-12") but not negative numbers
        const parts = value.split('-');
        if (parts.length === 2) {
          const start = parseInt(parts[0], 10);
          const end = parseInt(parts[1], 10);
          if (start < 0 || end < 0) {
            return;
          }
        }
      } else if (value && value.includes('+') && !value.startsWith('-')) {
        const numPart = value.replace('+', '');
        if (parseInt(numPart, 10) < 0) {
          return;
        }
      } else if (value && !Number.isNaN(Number(value))) {
        if (parseFloat(value) < 0) {
          return;
        }
      }

      // Normalize AMAP case-drift ("Amap" -> "AMAP").
      if (value !== '' && value !== null && value !== undefined) {
        const tokenResult = normalizeRepToken(String(value), ['AMAP']);
        if (tokenResult.isValid) {
          normalizedFieldValue = tokenResult.value;
        }
      }
    }

    setRows((prevRows) => {
      const fieldKey = keySwapDictionary[field];
      const writeValue = field === 'reps' ? normalizedFieldValue : value;

      const buildRow = (row) => {
        const updatedRow = { ...row, [fieldKey]: parseFieldValue(field, writeValue) };
        if (fieldKey === 'prescribedValue' && (writeValue === null || writeValue === '')) {
          delete updatedRow.prescribedValue;
        }
        return updatedRow;
      };

      // Auto-fill from set 1 only while the other sets are untouched or already
      // mirror set 1 — once a coach differentiates a set, stop overwriting it.
      const shouldAutoFill = rowIndex === 0 && writeValue !== null && writeValue !== undefined && writeValue !== '';
      if (shouldAutoFill) {
        const currentFirstSetValue = prevRows[0]?.[fieldKey];
        const otherSetsAreEmptyOrMatchCurrent = prevRows.slice(1).every((row) => {
          const cellValue = row[fieldKey];
          return cellValue === null || cellValue === undefined || cellValue === '' || cellValue === currentFirstSetValue;
        });

        if (otherSetsAreEmptyOrMatchCurrent) {
          return prevRows.map(buildRow);
        }
      }

      return prevRows.map((row, i) => (i === rowIndex ? buildRow(row) : row));
    });
  };

  // Helper function to validate key input for numbers-only fields
  // Pass allowRangeNotation=true for prescribed value, rest per rep, and intensity fields
  const isValidNumberKey = (key, currentValue, allowRangeNotation = false) => {
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return true;
    }

    if (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z') {
      return true;
    }

    // Allow decimal point; for range fields allow one per segment (split by '-')
    if (key === '.') {
      if (allowRangeNotation) {
        const currentSegment = currentValue.split('-').pop();
        if (!currentSegment.includes('.')) return true;
      } else if (!currentValue.includes('.')) {
        return true;
      }
    }

    if (/^\d$/.test(key)) {
      return true;
    }

    if (allowRangeNotation && (key === '-' || key === '+')) {
      return true;
    }

    return false;
  };

  // Helper function to validate key input for reps fields (allows AMAP and ranges)
  const isValidRepsKey = (key) => {
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return true;
    }

    if (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z') {
      return true;
    }

    if (/^\d$/.test(key)) {
      return true;
    }

    // Allow letters for AMAP
    if (/^[a-zA-Z]$/.test(key)) {
      return true;
    }

    // Allow dash for ranges (8-12) and plus for minimums (8+)
    if (key === '-' || key === '+') {
      return true;
    }

    return false;
  };

  const columnInputResolver = (name, rowIndex, row, key) => {
    const fieldValue = row[keySwapDictionary[name]];
    if (name === 'rest') {
      return (
        <RowValueContainer key={key}>
          <RowValue
            data-add-focus={`${domKey}-rest`}
            type='number'
            value={rest ?? ''}
            onChange={(e) => {
              const intRest = parseInt(e.target.value, 10);
              if (intRest < 0) return;
              setRest(Number.isNaN(intRest) ? null : intRest);
            }}
            onKeyDown={(e) => {
              // Prevent commas and validate for numbers-only field
              if (e.key === ',' || (!e.ctrlKey && !isValidNumberKey(e.key, e.target.value))) {
                e.preventDefault();
              }
            }}
            background={colors.surfaceSecondary}
          />
        </RowValueContainer>
      );
    }
    if (name === 'athlete input') {
      return (
        <RowValueContainer key={key}>
          <RowValue
            placeholder='-'
            /** disable the ability to edit this field */
            disabled
            background={colors.surfaceSecondary}
            textColor={colors.textTertiary}
          />
        </RowValueContainer>
      );
    }

    const isRangeField = RANGE_FIELDS.has(name) || name.includes('intensity') || name.includes('rest per rep');
    return (
      <RowValueContainer key={key}>
        <RowValue
          data-add-focus={rowIndex === 0 ? `${domKey}-${name}` : undefined}
          type='text' // "text" so range/AMAP tokens are accepted
          aria-label={`${name} for set ${rowIndex + 1}`}
          value={fieldValue ?? ''} // null/undefined -> '' (stay controlled); keep legit 0
          onChange={(e) => {
            handleInputChange(rowIndex, name, e.target.value);
          }}
          onKeyDown={(e) => {
            // Always prevent commas — multi-set values are entered per cell,
            // never as a comma-separated list.
            if (e.key === ',') {
              e.preventDefault();
              return;
            }

            // If the user is mutating an AMAP token they're sitting in the
            // middle of, wipe the whole token.
            if (name === 'reps') {
              const { value, selectionStart, selectionEnd } = e.target;
              const wipeResult = wipeRepKeywordOnMutation(
                e,
                value,
                selectionStart,
                selectionEnd,
                ['AMAP'],
                { backspaceDeletes: true },
              );
              if (wipeResult.wiped) {
                e.preventDefault();
                e.target.value = wipeResult.newValue;
                handleInputChange(rowIndex, name, wipeResult.newValue);
                const inputEl = e.target;
                requestAnimationFrame(() => {
                  inputEl.setSelectionRange(wipeResult.newCursor, wipeResult.newCursor);
                });
                return;
              }

              // Single-cell input: once the cell holds AMAP, block any
              // printable-char keystroke that would append/prepend.
              const cellTrimmed = (value || '').trim().toUpperCase();
              if (cellTrimmed === 'AMAP'
                  && selectionStart === selectionEnd
                  && typeof e.key === 'string'
                  && e.key.length === 1
                  && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                return;
              }
            }

            // Handle AMAP autocomplete for reps field - only when typing 'a' in empty field
            if (name === 'reps' && e.key.toLowerCase() === 'a' && (!e.target.value || e.target.value === '')) {
              e.preventDefault();
              e.target.value = 'AMAP';
              handleInputChange(rowIndex, name, 'AMAP');
              return;
            }

            // Use different validation for reps vs other fields
            const isValid = name === 'reps'
              ? isValidRepsKey(e.key)
              : isValidNumberKey(e.key, e.target.value, isRangeField);

            // Allow Ctrl combinations
            if (!e.ctrlKey && !isValid) {
              e.preventDefault();
            }
          }}
          background={colors.surfaceSecondary}
        />
      </RowValueContainer>
    );
  };

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.exercise?.unitAbbr) {
      return `(${unitContent?.exercise?.unitAbbr === 'wgt' ? 'lbs' : unitContent?.exercise?.unitAbbr})`;
    }
    return '';
  };

  const propertyTooltipId = (name) => {
    if (name === 'reps') return repsTooltipId;
    if (name === 'athlete input') return completionOnly ? undefined : athleteInputTooltipId;
    if (name === 'rest') return restTooltipId;
    return undefined;
  };

  /** Property label content (info icon + SwapPropDropdownSAQC), placed in the
   * transposed layout's label cell. */
  const renderPropertyLabel = (primaryRow) => {
    const tooltipId = propertyTooltipId(primaryRow.name);
    return (
      <NameAndDropdown>
        {tooltipId && (
          <IcomoonReact
            iconSet={iconSet}
            size={14}
            icon='info2'
            color={colors.textSecondary}
            style={{ cursor: 'pointer' }}
            data-tooltip-id={tooltipId}
            tabIndex={-1}
            focusable='false'
            aria-hidden='true'
          />
        )}
        <SwapPropDropdownSAQC
          visible={primaryRow.name !== 'athlete input'}
          setPrimaryRowSwitch={setPrimaryRowSwitch}
          primaryRowSwitch={primaryRowSwitch}
          rowName={primaryRow.name}
          size={12}
          updateColumnName={updateColumnName}
          setIntensityUnit={setIntensityUnit}
          setRestPerRepUnit={setRestPerRepUnit}
          name={(primaryRow?.label || capitalizeWords(primaryRow.name))}
          unit={primaryRow.name === 'prescribed value' ? findCorrectUnit(content) : null}
        />
      </NameAndDropdown>
    );
  };

  const tooltipStyle = {
    zIndex: 9999,
    backgroundColor: 'rgba(68, 68, 68, 1)',
    color: 'white',
    fontFamily: 'Nunito Sans',
    fontSize: '13px',
  };

  return (
    <Wrapper ref={tableContainerRef}>
      <SetsCountHeader>
        <SetsFieldGroup>
          <SetsCountLabel>Sets</SetsCountLabel>
          <SetsStepper>
            <StepperButton
              type='button'
              aria-label='Decrease sets'
              onClick={() => setSetCount(rows.length - 1)}
              disabled={rows.length <= 1}
            >
              −
            </StepperButton>
            <StepperInput
              type='text'
              inputMode='numeric'
              value={setsDraft !== null ? setsDraft : rows.length}
              onFocus={(e) => {
                setSetsDraft(String(rows.length));
                e.target.select();
              }}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                if (raw === '') {
                  setSetsDraft('');
                  return;
                }
                let value = parseInt(raw, 10);
                if (value > 10) value = parseInt(raw.slice(-1), 10);
                setSetsDraft(String(Math.min(10, Math.max(1, value))));
              }}
              onBlur={commitSetsDraft}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
              aria-label='Number of sets'
            />
            <StepperButton
              type='button'
              aria-label='Increase sets'
              onClick={() => setSetCount(rows.length + 1)}
              disabled={rows.length >= 10}
            >
              +
            </StepperButton>
          </SetsStepper>
          <SetsSubtext># of Sets: 1-10</SetsSubtext>
        </SetsFieldGroup>
        <StatusBadgeScroller>
          {coachCompletion && <StateBadge>Coach Completion</StateBadge>}
          {completionOnly && <StateBadge>Completion Only</StateBadge>}
        </StatusBadgeScroller>
      </SetsCountHeader>

      <TransposedScroll>
        <TransposedTable>
          <TransposedHeaderRow>
            <PropertyLabelCell />
            {rows.map((_, setIdx) => (
              <SetHeaderCell key={`set-header-${setIdx}`}>
                {`Set ${setIdx + 1}`}
              </SetHeaderCell>
            ))}
          </TransposedHeaderRow>

          {primaryRows.map((primaryRow) => {
            if (primaryRow.name === 'sets') return null;
            if (!primaryRowSwitch[primaryRow.name]) return null;
            return (
              <TransposedRow key={`prop-${primaryRow.name}`}>
                <PropertyLabelCell>
                  {renderPropertyLabel(primaryRow)}
                </PropertyLabelCell>
                {rows.map((row, rowIndex) => (
                  <SetCell key={`cell-${primaryRow.name}-${rowIndex}`}>
                    {columnInputResolver(primaryRow.name, rowIndex, row, `${primaryRow.name}-${rowIndex}`)}
                  </SetCell>
                ))}
              </TransposedRow>
            );
          })}
        </TransposedTable>
      </TransposedScroll>

      <TransposedFooter>
        {showAddColumnButton && (
          <StaticAddColumnDropdownSimple
            primaryRows={primaryRows}
            primaryRowSwitch={primaryRowSwitch}
            setPrimaryRowSwitch={setPrimaryRowSwitch}
            setPrimaryRowSwitchState={setPrimaryRowSwitchState}
            setIntensityUnit={setIntensityUnit}
            size={12}
            assignedId={content?.assignedId}
            onPropertyAdded={focusAddedProperty}
            trigger={(
              <AddPropertyTrigger>
                <AddPropertyTriggerIcon>
                  <IcomoonReact iconSet={iconSet} size={10} icon='plus' color='white' />
                </AddPropertyTriggerIcon>
                Add Property
              </AddPropertyTrigger>
            )}
          />
        )}
      </TransposedFooter>

      <ReactTooltip
        id={repsTooltipId}
        place='top'
        positionStrategy='fixed'
        appendTo={document.body}
        style={{ ...tooltipStyle, maxWidth: '250px', padding: '10px 16px' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>
              <Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 5)</Text>
            </li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 8-12)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 8+)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;AMAP&quot; for as many as possible</Text></li>
          </ul>
        </div>
      </ReactTooltip>

      <ReactTooltip
        id={athleteInputTooltipId}
        place='top'
        positionStrategy='fixed'
        content='Athlete input only'
        style={tooltipStyle}
      />

      <ReactTooltip
        id={restTooltipId}
        place='top'
        positionStrategy='fixed'
        content='Rest value will be the same for all sets.'
        style={tooltipStyle}
      />
    </Wrapper>
  );
};

ExerciseTableTransposedSAQCLocal.propTypes = {
  content: PropTypes.instanceOf(Object).isRequired,
  initialSets: PropTypes.instanceOf(Array),
  onChange: PropTypes.func.isRequired,
  coachCompletion: PropTypes.bool,
  completionOnly: PropTypes.bool,
};

ExerciseTableTransposedSAQCLocal.defaultProps = {
  initialSets: null,
  coachCompletion: false,
  completionOnly: false,
};

export default ExerciseTableTransposedSAQCLocal;
